import React, { memo } from 'react';

import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';

import i18n from '../messages.json';

/**
 * Warning shown when the editor tries to schedule a push to the SAME segments at
 * the SAME time as one that's already scheduled (WWW-15236 #6). It's a soft
 * guard, not a block — it surfaces the existing push(es) so the editor can decide
 * to schedule another one anyway or go back.
 *
 * Props
 *   show:      boolean
 *   conflicts: [{ id, title, body, link, segments, time }] — existing colliding pushes
 *   isBusy:    boolean — scheduling in flight (disables the buttons)
 *   onConfirm: () => void — "Schedule anyway"
 *   onCancel:  () => void — "Go back"
 */
const ScheduleConflictDialog = ({ show, conflicts = [], isBusy = false, onConfirm, onCancel }) => {
  const labels = i18n.ink.notification.scheduled.conflict;

  return (
    <Modal
      show={show}
      onHide={() => (isBusy ? null : onCancel())}
      centered
      dialogClassName='schedule-conflict-dialog'
    >
      <Modal.Header closeButton>
        <Modal.Title>{labels.title}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p className='schedule-conflict__intro'>
          {conflicts.length > 1 ? labels.introPlural : labels.intro}
        </p>

        <div className='schedule-conflict__list'>
          {conflicts.map((c) => (
            <div key={c.id} className='schedule-conflict__card'>
              <div className='schedule-conflict__title'>{c.title || labels.untitled}</div>
              {c.body && <div className='schedule-conflict__body'>{c.body}</div>}
              <dl className='schedule-conflict__meta'>
                <div>
                  <dt>{labels.segments}</dt>
                  <dd>{c.segments || '—'}</dd>
                </div>
                {c.link && (
                  <div>
                    <dt>{labels.link}</dt>
                    <dd className='schedule-conflict__link'>{c.link}</dd>
                  </div>
                )}
                <div>
                  <dt>{labels.time}</dt>
                  <dd>{c.time}</dd>
                </div>
              </dl>
            </div>
          ))}
        </div>

        <p className='schedule-conflict__question'>{labels.question}</p>
      </Modal.Body>

      <Modal.Footer>
        <Button variant='light' onClick={onCancel} disabled={isBusy} style={{ marginRight: 'auto' }}>
          {labels.goBack}
        </Button>
        <Button variant='primary' onClick={onConfirm} disabled={isBusy}>
          {labels.scheduleAnyway}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default memo(ScheduleConflictDialog);
